import { useMemo, useState } from 'react';

import updateTicketQuantity from '../helpers/updateTicketQuantity';
import toggleTicketTier from '../helpers/toggleTicketTier';
import {
  NormalizedTickets,
  QuantityChangeEvents,
  ToggleTicketTierFn,
  UpdateQuantityFn
} from '../types';
import { EventForUser } from '../../../../types';
import { MAX_TICKETS as DEFAULT_MAX_TICKETS } from 'legacy/tickets/service';

/**
 * Creates and manages selected tickets for an event.
 *
 * Implementation note:
 * Selected tickets are stored as a very basic normalized state instead of a collection.
 * This allows us to update a ticket's metadata without having to do complex computations or parsing of a collection.
 */
const useTickets = (event: EventForUser) => {
  const [selectedTicketTiers, setSelectedTicketTiers] =
    useState<NormalizedTickets>({});
  const [tierRequiringApproval, setTierRequiringApproval] = useState<
    string | null
  >(null);

  const maxTicketsPerTransaction =
    event.maxTicketsPerTransaction || DEFAULT_MAX_TICKETS;

  /**
   * Calculates the total amount of tickets selected across all ticket tiers.
   */
  const totalAmountOfTicketsSelected = useMemo(
    () =>
      Object.values(selectedTicketTiers).reduce(
        (total, ticket) => total + ticket.quantity,
        0
      ),
    [selectedTicketTiers]
  );

  /**
   * Updates the quantity of a selected ticket tier.
   */
  const updateQuantity: UpdateQuantityFn = (
    ticketId,
    updateEvent,
    checkoutUpdater
  ) => {
    // If total amount of ticket selected is equal or above the max amount allowed per transaction,
    // AND user is trying to add more tickets to the transaction, do not update quantity.
    if (
      totalAmountOfTicketsSelected >= maxTicketsPerTransaction &&
      updateEvent === QuantityChangeEvents.Increase
    ) {
      return;
    }

    const tickets = updateTicketQuantity(
      selectedTicketTiers,
      ticketId,
      updateEvent
    );

    if (checkoutUpdater) {
      checkoutUpdater(tickets).then(() => setSelectedTicketTiers(tickets));
    } else {
      setSelectedTicketTiers(tickets);
    }
  };

  /**
   * Toggles the selected ticket tier in the list of selected tiers.
   */
  const onToggleTicketTier: ToggleTicketTierFn = (
    toggledTicketId,
    checkoutUpdater
  ) => {
    // If total amount of ticket selected is equal or above the max amount allowed per transaction, do not add the ticket tier to the checkout
    if (totalAmountOfTicketsSelected >= maxTicketsPerTransaction) {
      return;
    }

    const tickets = toggleTicketTier(selectedTicketTiers, toggledTicketId);

    if (checkoutUpdater) {
      checkoutUpdater(tickets).then(() => setSelectedTicketTiers(tickets));
    } else {
      setSelectedTicketTiers(tickets);
    }
  };

  const onToggleTierRequiringApproval = (tierId: string) => {
    // If total amount of ticket selected is equal or above the max amount allowed per transaction, do not add the ticket tier to the checkout
    if (totalAmountOfTicketsSelected >= maxTicketsPerTransaction) {
      return;
    }

    setTierRequiringApproval((prevTier) => (prevTier ? null : tierId));
  };

  return {
    maxTicketsPerTransaction,
    selectedTicketTiers,
    toggleTicketTier: onToggleTicketTier,
    totalAmountOfTicketsSelected,
    updateQuantity,
    tierRequiringApproval,
    toggleTierRequiringApproval: onToggleTierRequiringApproval
  };
};

export default useTickets;
